import type {StateCreator} from 'zustand'
import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import type {CodeownersSlice} from './codeowners'
import type {DiffFilterSlice} from './diff-filter'
import {getFileExtension} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {getPathOwnership} from '../../page-data/loaders/use-codeowners-data'
import type {DiffSummariesSlice} from './diff-summaries'
import type {ProgressiveDiffsSlice} from './progressive-diffs'
import {forceAnnouncementToScreenReaders} from '../../utils/force-announcement-to-screen-reader'

export type HiddenFilesSlice = {
  hiddenFiles: Map<string, boolean>
  filteredDiffSummaries: PullRequestFileTreeDiff[]
} & HiddenFilesSliceActions

export type HiddenFilesSliceActions = {
  _updateHiddenFiles: () => void
}

export const createHiddenFilesSlice =
  (): StateCreator<
    HiddenFilesSlice & DiffSummariesSlice & CodeownersSlice & DiffFilterSlice & ProgressiveDiffsSlice,
    [['zustand/devtools', never]],
    [],
    HiddenFilesSlice
  > =>
  (set, get) => ({
    hiddenFiles: new Map<string, boolean>(),
    filteredDiffSummaries: [],
    _updateHiddenFiles: () => {
      // Required state
      const diffSummaries = get().diffSummaries
      const codeowners = get().codeowners
      const filters = get().filterState

      // state to update
      const hiddenFiles = new Map<string, boolean>()
      const filteredDiffSummaries: PullRequestFileTreeDiff[] = []

      for (const diffSummary of diffSummaries ?? []) {
        const isOwnedByViewer = getPathOwnership({
          diffPath: diffSummary.path,
          codeownersData: codeowners,
        }).isOwnedByViewer

        if (matchesFilter(diffSummary, filters, isOwnedByViewer)) {
          filteredDiffSummaries.push(diffSummary)
          hiddenFiles.set(diffSummary.path, false)
        } else {
          hiddenFiles.set(diffSummary.path, true)
        }
      }

      // Only announce filter results on user interaction (and not on page load).
      if (get().filtersInteractedWith) {
        const filteredCount = filteredDiffSummaries.length
        const announcementText = `${filteredCount} file${filteredCount === 1 ? '' : 's'} remain${
          filteredCount === 1 ? 's' : ''
        }`
        forceAnnouncementToScreenReaders(announcementText, 150)
      }

      set({hiddenFiles, filteredDiffSummaries}, undefined, 'updateHiddenFiles')
    },
  })

function matchesFilter(
  diff: PullRequestFileTreeDiff,
  filters: DiffFilterSlice['filterState'],
  isOwnedByViewer: boolean,
): boolean {
  const match =
    diff.path.toLowerCase().includes(filters.filterText.toLowerCase()) &&
    !filters.unselectedFileExtensions.has(getFileExtension(diff.path)) &&
    (filters.showDeletedFiles || !(diff.changeType === 'REMOVED' || diff.changeType === 'DELETED')) &&
    (!filters.showOnlyManifestFiles || !!diff.isManifestFile) &&
    (!filters.showOnlyOwnedFiles || isOwnedByViewer) &&
    (filters.showViewedFiles || !diff.markedAsViewed) &&
    (filters.showVendoredFiles || !diff.isVendored)

  return match
}
